import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DistributorActivity } from './DistributorActivity';
import DistributorOrderForm from './DistributorOrderForm';
import DistributorEntryDialog from './DistributorEntryDialog';

export default function DistributorOrders({
  clinics,
  loading,
  settingsRevisions,
  refresh,
  onSubmitted,
  active = true,
  headingRef,
  readOnly = false,
  t,
}) {
  const [open, setOpen] = useState(false);
  const [clinicId, setClinicId] = useState('');
  const [discardAction, setDiscardAction] = useState(null);
  const [orderState, setOrderState] = useState({ busy: false, uncertain: false, dirty: false });
  const locked = orderState.busy || orderState.uncertain;
  const cancel = () => {
    if (locked) return;
    if (clinicId && orderState.dirty) setDiscardAction('close');
    else {
      setClinicId('');
      setOpen(false);
    }
  };
  return (
    <div className="space-y-5">
      <DistributorActivity
        activityKind="orders"
        t={t}
        active={active}
        refresh={refresh}
        pageHeader={{
          headingRef,
          createAction: !readOnly && (
            <button
              type="button"
              className="me-dw-primary me-dw-create-action"
              disabled={loading || !clinics.length || open}
              onClick={() => setOpen(true)}
            >
              <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                add
              </span>
              {t('New order')}
            </button>
          ),
        }}
      >
        {!readOnly && !loading && !clinics.length && (
          <p className="me-dw-card p-5 text-sm text-slate-600">
            {t('Add a customer before creating an order.')}{' '}
            <Link to="/distributor/customers" className="me-dw-link">
              {t('Go to Customers')}
            </Link>
          </p>
        )}
        {!readOnly && open && (
          <DistributorEntryDialog
            open={active && open}
            title="New Order"
            locked={locked}
            onClose={cancel}
            t={t}
          >
            <div className="me-dw-card p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {!clinicId ? (
                    <label className="block text-sm font-semibold text-slate-700">
                      {t('Choose a customer')}
                      <select
                        className="me-dw-field mt-2 block w-full max-w-md rounded-lg border px-3 py-2"
                        value=""
                        onChange={(event) => setClinicId(event.target.value)}
                      >
                        <option value="">{t('Select customer')}</option>
                        {clinics.map((clinic) => (
                          <option key={clinic._id} value={clinic._id}>
                            {clinic.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-semibold text-slate-800">
                        {clinics.find((clinic) => clinic._id === clinicId)?.name}
                      </p>
                      <button
                        type="button"
                        className="me-dw-link text-sm"
                        disabled={locked}
                        onClick={() => {
                          if (locked) return;
                          if (orderState.dirty) setDiscardAction('customer');
                          else setClinicId('');
                        }}
                      >
                        {t('Change customer')}
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="me-dw-link text-sm"
                  disabled={locked}
                  onClick={cancel}
                >
                  {t('Cancel')}
                </button>
              </div>
              {orderState.uncertain && (
                <p role="status" className="mt-3 text-sm text-amber-800">
                  {t(
                    'Keep this order open until its result is confirmed. Retry uses the same request.'
                  )}
                </p>
              )}
              {discardAction && (
                <div
                  role="region"
                  aria-label={t('Discard order draft?')}
                  className="me-dw-subpanel mt-4 p-4"
                >
                  <p className="text-sm">
                    {t(
                      discardAction === 'close'
                        ? 'Closing discards this unsubmitted order draft.'
                        : 'Changing customers discards this unsubmitted order draft.'
                    )}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button
                      type="button"
                      className="me-dw-primary"
                      onClick={() => setDiscardAction(null)}
                    >
                      {t('Keep editing')}
                    </button>
                    <button
                      type="button"
                      className="me-dw-link"
                      disabled={locked}
                      onClick={() => {
                        if (locked) return;
                        setClinicId('');
                        if (discardAction === 'close') setOpen(false);
                        setDiscardAction(null);
                        setOrderState({ busy: false, uncertain: false, dirty: false });
                      }}
                    >
                      {t('Discard draft')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            {clinicId && (
              <DistributorOrderForm
                inDialog
                key={clinicId}
                clinicId={clinicId}
                settingsRevision={settingsRevisions[clinicId] || 0}
                onStateChange={setOrderState}
                t={t}
                onSubmitted={onSubmitted}
              />
            )}
          </DistributorEntryDialog>
        )}
      </DistributorActivity>
    </div>
  );
}
